import React from "react";
import { Box, Icons, Typography, Stack } from '@curves/ui-base';
import { Grid } from "@mui/material";
import clsx from "clsx";
import { formatVal } from "@Util/utils";


/**
 * WeightManagementCard - 體重管理指南卡片組件
 *
 * 顯示目前體重、目標體重以及體重控制、脂肪控制、肌肉控制的調整目標數值。
 *
 * @component
 * @param {Object} props - 組件屬性
 * @param {string} [props.className] - 額外的 CSS 類別名稱
 * @param {number} props.currentWeightKg - 目前體重（公斤）
 * @param {number} props.targetWeightKg - 目標體重（公斤）
 * @param {string} [props.targetWeightColorTheme='default'] - 目標體重的顏色主題，可用值：'default'、'primary'、'secondary'、'success'、'error'、'warning'、'info'
 * @param {Object} [props.weightControl] - 體重控制目標
 * @param {string} [props.weightControl.theme] - 體重控制的顏色主題，可用值：'positive'、'negative'、'default'
 * @param {number} [props.weightControl.valueKg] - 體重控制目標數值（公斤），正數表示增加，負數表示減少
 * @param {Object} [props.bfmControl] - 脂肪控制目標
 * @param {string} [props.bfmControl.theme] - 脂肪控制的顏色主題，可用值：'positive'、'negative'、'default'
 * @param {number} [props.bfmControl.valueKg] - 脂肪控制目標數值（公斤），正數表示增加，負數表示減少
 * @param {Object} [props.ffmControl] - 肌肉控制目標
 * @param {string} [props.ffmControl.theme] - 肌肉控制的顏色主題，可用值：'positive'、'negative'、'default'
 * @param {number} [props.ffmControl.valueKg] - 肌肉控制目標數值（公斤），正數表示增加，負數表示減少
 * @returns {JSX.Element} WeightManagementCard 組件
 *
 * @example
 * ```jsx
 * <WeightManagementCard
 *   currentWeightKg={65.2}
 *   targetWeightKg={63.0}
 *   targetWeightColorTheme="default"
 *   adjustmentGoals={{
 *     weightControl: {
 *       theme: 'negative',
 *       valueKg: -2.2
 *     },
 *     bfmControl: {
 *       theme: 'negative',
 *       valueKg: -1.5
 *     },
 *     ffmControl: {
 *       theme: 'positive',
 *       valueKg: 0.7
 *     }
 *   }}
 * />
 * ```
 */
const WeightManagementCard = ({ className, currentWeightKg, targetWeightKg, targetWeightColorTheme = "default", weightControl = {}, bfmControl = {}, ffmControl = {} }) => {
	return (
		<Box className={clsx("weight-management-card", className)}>
			{/* <Typography className="title">體重管理指南</Typography> */}
			<Stack className="comparison-box" direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
				<Box className="item">
					<Typography className="label">目前體重</Typography>
					<Typography className="value">{currentWeightKg}</Typography>
					<Box className="unit">kg</Box>
				</Box>
				<Box className="arrow">
					<Icons.ArrowUp />
				</Box>
				<Box className="item">
					<Typography className="label">目標體重</Typography>
					<Typography className={clsx("value", targetWeightColorTheme)}>{targetWeightKg}</Typography>
					<Box className="unit">kg</Box>
				</Box>
			</Stack>
			<Grid className="control-grid" container spacing={2}>
				<Grid className="col" xs={4} item>
					<Box className="item">
						<Typography className="label">體重控制</Typography>
						<Typography className={clsx("value", weightControl?.theme)}>{formatVal(weightControl)}</Typography>
						<Box className="unit">kg</Box>
					</Box>
				</Grid>
				<Grid className="col" xs={4} item>
					<Box className="item">
						<Typography className="label">脂肪控制</Typography>
						<Typography className={clsx("value", bfmControl?.theme)}>{formatVal(bfmControl)}</Typography>
						<Box className="unit">kg</Box>
					</Box>
				</Grid>
				<Grid className="col" xs={4} item>
					<Box className="item">
						<Typography className="label">肌肉控制</Typography>
						<Typography className={clsx("value", ffmControl?.theme)}>{formatVal(ffmControl)}</Typography>
						<Box className="unit">kg</Box>
					</Box>
				</Grid>
			</Grid>
		</Box>
	);
};

export default WeightManagementCard;
